import React, {FC, useState, useCallback, useEffect} from 'react'
import {Form, Formik, FormikProps} from 'formik'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {ToastContainer} from 'react-toastify' 
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import FormTabHeader, {TabOption} from '../../../../shared/components/FormTabHeader'
import TextInput from '../../../../shared/components/TextInput'
import {IBafModel, initialBafModel, USER_PURPUSE, validationBafSchema} from './BafModel'
import {BafTable} from './BafTable'
import SelectInput from '../../../../shared/components/SelectInput'
import {branches as fetchBranches} from '../../../../shared/models/SharedApiLookups'
import TextAreaInput from '../../../../shared/components/TextAreaInput'
import {STATUS_OPTION} from './BafModel'
import CheckboxInput from '../../../../shared/components/CheckboxInput'
import {loadBafs, updateBaf, createBaf, deleteBaf} from './Api'
import {vehicleTypes} from '../../../../shared/models/SharedApiLookups'
import AsyncSelectInput from '../../../../shared/components/AsyncSelectInputBaf'

// Tip güvenliği için tab seçeneklerini burada da tanımlayabiliriz
type BafTab = 'baf' | 'personnel' | 'vehicle' | 'return' | 'list'

const BafPage: FC = () => {
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [bafEntries, setBafEntries] = useState<IBafModel[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [tab, setTab] = useState<string | 'baf'>('baf')
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc') 
  const [branches, setBranches] = useState<{value: string; label: string}[]>([])
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState<{value: string; label: string}[]>([])

  useEffect(() => {
    vehicleTypes().then(setVehicleTypeOptions)
  }, [])

  // Define tabs for the BAF form sections
  const tabOptions: TabOption<BafTab>[] = [
    {key: 'baf', title: 'BAF İşlemleri', icon: 'bi bi-info-circle'},
    {key: 'personnel', title: 'Personel Bilgileri', icon: 'bi bi-person'},
    {key: 'vehicle', title: 'Araç Bilgileri', icon: 'bi bi-car-front'},
    {key: 'return', title: 'Dönüş Bilgileri', icon: 'bi bi-arrow-return-left'},
    {key: 'list', title: 'Baf Listesi', icon: 'bi bi-list'},
  ]

  useEffect(() => {
    async function fetchBranchesData() {
      const branchList = await fetchBranches() // ✅ API fonksiyonu çağırıldı
      setBranches(branchList)
    }

    fetchBranchesData()
  }, [])

  const fetchBafEntries = useCallback(
    async (page: number = 0, sort?: string, order?: 'asc' | 'desc') => {
      setLoading(true) 

      try {
        const response = await loadBafs(page, 5, sort, order)
        if (response.data.hasError) {
          throw new Error(response.data.message || 'Failed to fetch bafs')
        }
        setBafEntries(response.data.payload.content)
        setTotalPages(response.data.payload.totalPages)
      } catch (err) { 
        showErrorToast('BAF kayıtları yüklenirken bir hata oluştu')
      } finally {
        setLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    if (tab === 'list') {
      fetchBafEntries(0)
    }
  }, [tab, fetchBafEntries])

  const handleSave = async (values: IBafModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updateBaf({...values, id: selectedId})
        showSuccessToast('BAF kaydı başarıyla güncellendi')
      } else {
        await createBaf(values)
        showSuccessToast('BAF kaydı başarıyla oluşturuldu')
      }
      setCurrentPage(0)
      await fetchBafEntries(0)
      resetForm()
      setSelectedId(null)
      setTab('list')
    } catch (err) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = useCallback((bafEntry: IBafModel, formik: FormikProps<IBafModel>) => {
    formik.setValues(bafEntry)
    setSelectedId(bafEntry.id)
    setTab('baf')
  }, [])

  const handleDeleteClick = useCallback((bafEntry: IBafModel) => {
    setSelectedId(bafEntry.id)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (selectedId) {
      setLoading(true)
      try {
        await deleteBaf(selectedId)
        showSuccessToast('BAF kaydı başarıyla silindi')
        await fetchBafEntries(currentPage)
      } catch (err) {
        console.error(err)
        showErrorToast('BAF kaydı silinirken bir hata oluştu')
      } finally {
        setLoading(false)
        setShowModal(false)
        setSelectedId(null)
      }
    }
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchBafEntries(newPage, sortBy, sortOrder)
      }
    },
    [totalPages, sortBy, sortOrder, fetchBafEntries]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      fetchBafEntries(currentPage, column, order)
    },
    [currentPage, fetchBafEntries]
  )

  return (
    <>
      <Formik
        initialValues={initialBafModel}
        validationSchema={validationBafSchema}
        enableReinitialize
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
            setTab('baf')
          }

          return (
            <>
              <FormTabHeader
                formId='bafForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab} // Hatanın giderildiği satır
                setSelectedId={setSelectedId}
                tabs={tabOptions}
                onExportExcel={() => console.log('Excel export')}
              />
              <div className='card mb-5'>
                {/* Form Tabları */}
                {tab !== 'list' && (
                  <Form id='bafForm' className='form card-body'>
                    {/* BAF İşlemleri Sekmesi */}
                    {tab === 'baf' && (
                      <div className='row mb-6'>
                        <TextInput
                          name='operationDate'
                          label='İşlem Tarihi'
                          wrapperClassName='col-md-6'
                          type='date'
                        />
                        <SelectInput
                          name='userPurpose'
                          label='Kullanım Amacı'
                          wrapperClassName='col-md-6'
                          options={USER_PURPUSE}
                        />
                        <TextInput
                          name='departureDate'
                          label='Çıkış Tarihi'
                          wrapperClassName='col-md-6'
                          type='date'
                        />
                        <SelectInput
                          name='status'
                          label='Durum'
                          wrapperClassName='col-md-6'
                          options={STATUS_OPTION}
                        />
                        <TextInput
                          name='departureTime'
                          label='Çıkış Saati'
                          wrapperClassName='col-md-6'
                          type='time'
                        />
                        <AsyncSelectInput
                          name='approvedByFullName'
                          label='Onaylayan'
                          wrapperClassName='col-md-6'
                          fetchUrl='/api/v1/personnel/search'
                        />
                        <SelectInput
                          name='departureBranch'
                          label='Çıkış Şubesi'
                          wrapperClassName='col-md-6'
                          options={branches}
                        />
                        <TextAreaInput
                          name='description'
                          label='Açıklama'
                          wrapperClassName='col-md-6'
                        />
                        <AsyncSelectInput
                          name='processedByFullName'
                          label='İşlemi Yapan'
                          wrapperClassName='col-md-6'
                          fetchUrl='/api/v1/personnel/search'
                        />
                        <div className='col-md-6'></div>
                        <CheckboxInput
                          name='rentable'
                          label='Kiraya Verilebilir'
                          validate={(value) => {
                            if (!value) return 'Bu alanı işaretlemelisiniz'
                          }}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                    )}
                    {/* Personel Bilgileri Sekmesi */}
                    {tab === 'personnel' && (
                      <div className='row mb-6'>
                        <AsyncSelectInput
                          name='personnelName'
                          label='Personel Adı'
                          wrapperClassName='col-md-6'
                          fetchUrl='/api/v1/personnel/search'
                        />
                        <AsyncSelectInput
                          name='personnelSurname'
                          label='Personel Soyadı'
                          wrapperClassName='col-md-6'
                          fetchUrl='/api/v1/personnel/search'
                        />
                      </div>
                    )}
                    {/* Araç Bilgileri Sekmesi */}
                    {tab === 'vehicle' && (
                      <div className='row mb-6'>
                        <SelectInput
                          name='vehicleType'
                          label='Araç Tipi'
                          options={vehicleTypeOptions}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                    )}

                    {/* Dönüş Bilgileri Sekmesi */}
                    {tab === 'return' && (
                      <div className='row mb-6'>
                        <TextInput
                          name='returnDate'
                          label='Dönüş Tarihi'
                          wrapperClassName='col-md-6'
                          type='date'
                        />
                        <SelectInput
                          name='returnBranch'
                          label='Dönüş Şubesi'
                          wrapperClassName='col-md-6'
                          options={branches}
                        />
                        <TextInput
                          name='returnTime'
                          label='Dönüş Saati'
                          wrapperClassName='col-md-6'
                          type='time'
                        />
                        <TextInput
                          name='returnKm'
                          label='Dönüş KM'
                          wrapperClassName='col-md-6'
                          type='number'
                        />
                        <div className='col-md-6'></div>
                        <TextInput
                          name='returnFuel'
                          label='Dönüş Yakıt'
                          wrapperClassName='col-md-6'
                          type='number'
                        />
                      </div>
                    )}
                  </Form>
                )}
                {/* Liste Sekmesi */}
                {tab === 'list' && (
                  <div className='card'>
                    <div className='card-body pt-0'>
                      <BafTable
                        data={bafEntries}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        onEdit={(bafEntry) => handleEdit(bafEntry, formik)}
                        onDelete={handleDeleteClick}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        loading={loading}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )
        }}
      </Formik>
      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Bu BAF kaydını silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />
      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}

export {BafPage}
